/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Locale } from "../types";

const messages = {
  en: {
    commandCenter: "Command Center",
    liveOperations: "Live Operations",
    orders: "Orders",
    search: "Search orders, restaurants, drivers…",
    attention: "Needs attention",
    live: "Live",
    today: "Today · Tunisia",
    overview: "Operational overview",
  },
  fr: {
    commandCenter: "Centre de commande",
    liveOperations: "Opérations en direct",
    orders: "Commandes",
    search: "Rechercher commandes, restaurants, livreurs…",
    attention: "À surveiller",
    live: "En direct",
    today: "Aujourd’hui · Tunisie",
    overview: "Vue opérationnelle",
  },
  ar: {
    commandCenter: "مركز القيادة",
    liveOperations: "العمليات المباشرة",
    orders: "الطلبات",
    search: "ابحث عن طلب أو مطعم أو سائق…",
    attention: "يتطلب الانتباه",
    live: "مباشر",
    today: "اليوم · تونس",
    overview: "نظرة عامة على العمليات",
  },
} as const;
type Key = keyof typeof messages.en;
const I18nContext = createContext({
  locale: "en" as Locale,
  setLocale: (locale: Locale) => {
    void locale;
  },
  t: (key: Key) => messages.en[key] as string,
});
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(
    () => (localStorage.getItem("locale") as Locale) || "en",
  );
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    localStorage.setItem("locale", locale);
  }, [locale]);
  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: Key) => messages[locale][key] as string,
    }),
    [locale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
export const useI18n = () => useContext(I18nContext);
